import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { ChatList } from "@/features/chat/components/ChatList";

const CHAT_BAR_MIN_WIDTH = 220;
const CHAT_BAR_MAX_WIDTH = 320;
const CHAT_BAR_KEYBOARD_STEP = 8;
const CHAT_BAR_STORAGE_KEY = "rulewatcher:chat-bar-width";

const clampChatBarWidth = (width: number) =>
  Math.min(CHAT_BAR_MAX_WIDTH, Math.max(CHAT_BAR_MIN_WIDTH, width));

const getInitialChatBarWidth = () => {
  if (typeof window === "undefined") return CHAT_BAR_MAX_WIDTH;
  try {
    const storedValue = window.localStorage.getItem(CHAT_BAR_STORAGE_KEY);
    if (storedValue === null) return CHAT_BAR_MAX_WIDTH;
    const storedWidth = Number(storedValue);
    return Number.isFinite(storedWidth)
      ? clampChatBarWidth(storedWidth)
      : CHAT_BAR_MAX_WIDTH;
  } catch {
    return CHAT_BAR_MAX_WIDTH;
  }
};

export const Route = createFileRoute("/chat-room")({
  component: ChatRoomLayout,
});

function ChatRoomLayout() {
  const chatBarRef = useRef<HTMLElement>(null);
  const chatBarWidthRef = useRef(getInitialChatBarWidth());
  const previousUserSelectRef = useRef("");
  const [chatBarWidth, setChatBarWidth] = useState(chatBarWidthRef.current);
  const [isResizing, setIsResizing] = useState(false);

  const updateChatBarWidth = (width: number, persist = false) => {
    const nextWidth = clampChatBarWidth(Math.round(width));
    chatBarWidthRef.current = nextWidth;
    setChatBarWidth(nextWidth);
    if (persist && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(CHAT_BAR_STORAGE_KEY, String(nextWidth));
      } catch {
        // Resizing remains available when browser storage is unavailable.
      }
    }
  };

  const finishResize = (event?: PointerEvent<HTMLDivElement>) => {
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsResizing(false);
    updateChatBarWidth(chatBarWidthRef.current, true);
    if (typeof document !== "undefined") {
      document.body.style.userSelect = previousUserSelectRef.current;
    }
  };

  useEffect(
    () => () => {
      if (typeof document !== "undefined") {
        document.body.style.userSelect = previousUserSelectRef.current;
      }
    },
    [],
  );

  return (
    <div className="flex h-full min-h-0 bg-white">
      <aside
        ref={chatBarRef}
        className="relative h-full shrink-0 border-r border-slate-200 bg-white"
        style={{ width: chatBarWidth }}
      >
        <ChatList
          containerHeight="100%"
          containerClassName="h-full rounded-none border-0 shadow-none"
        />
        <hr
          aria-label="Resize chat bar"
          aria-orientation="vertical"
          aria-valuemin={CHAT_BAR_MIN_WIDTH}
          aria-valuemax={CHAT_BAR_MAX_WIDTH}
          aria-valuenow={chatBarWidth}
          tabIndex={0}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            setIsResizing(true);
            previousUserSelectRef.current = document.body.style.userSelect;
            document.body.style.userSelect = "none";
          }}
          onPointerMove={(event) => {
            if (!isResizing || !chatBarRef.current) return;
            const left = chatBarRef.current.getBoundingClientRect().left;
            updateChatBarWidth(event.clientX - left);
          }}
          onPointerUp={finishResize}
          onPointerCancel={finishResize}
          onDoubleClick={() => updateChatBarWidth(CHAT_BAR_MAX_WIDTH, true)}
          onKeyDown={(event) => {
            let nextWidth: number | null = null;
            if (event.key === "ArrowLeft") {
              nextWidth = chatBarWidth - CHAT_BAR_KEYBOARD_STEP;
            } else if (event.key === "ArrowRight") {
              nextWidth = chatBarWidth + CHAT_BAR_KEYBOARD_STEP;
            } else if (event.key === "Home") {
              nextWidth = CHAT_BAR_MIN_WIDTH;
            } else if (event.key === "End") {
              nextWidth = CHAT_BAR_MAX_WIDTH;
            }
            if (nextWidth === null) return;
            event.preventDefault();
            updateChatBarWidth(nextWidth, true);
          }}
          className={`absolute -right-1 top-0 z-30 h-full w-2 cursor-col-resize touch-none border-0 border-l-2 outline-none transition-colors hover:border-rw-main focus-visible:border-rw-main ${
            isResizing ? "border-rw-main" : "border-transparent"
          }`}
        />
      </aside>
      <main className="min-w-0 flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
